import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { SelectionChips } from '../SelectionChips/SelectionChips';
import { SelectionPanel } from '../SelectionPanel/SelectionPanel';
import { SelectionTrigger } from '../SelectionTrigger/SelectionTrigger';

import { type MultiSelectProps } from './MultiSelect.types';
import { useMultiSelectLogic } from './useMultiSelectLogic';

/**
 * Several values out of a list the caller already holds — the control behind a
 * `multi-select` field and a `multi-select` setting definition (W8).
 *
 * Not `ServerSelect`: the options here are a closed set the caller already has
 * and already translated, so there is nothing to search on a server and no label
 * to resolve by id. Both controls draw the same `SelectionPanel` from the same
 * `SelectionTrigger`, so the keyboard story, the empty state and the render cap
 * cannot drift apart.
 *
 * A plain button rather than a `combobox`: this trigger shows a COUNT and keeps
 * its values in chips beside it, where `ServerSelect`'s shows the value itself.
 *
 * Chosen values stay visible as removable chips, so one can come out without
 * reopening the list.
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(function MultiSelect(
  {
    id,
    className,
    size,
    placeholder,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    maxVisibleChips = 4,
    hasChips = true,
    onBlur,
    'aria-describedby': describedBy,
    'aria-labelledby': labelledBy,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const panelId = useId();
  const logic = useMultiSelectLogic({ ...rest, isDisabled, isReadOnly });

  return (
    <div className={cn('flex flex-col gap-sm', className)}>
      <SelectionTrigger
        ref={ref}
        id={id}
        panelId={panelId}
        isOpen={logic.isOpen}
        size={size}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        placeholder={placeholder ?? t('multiSelect.placeholder')}
        summary={logic.summary}
        onBlur={onBlur}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        clear={
          logic.isClearVisible ? { label: t('multiSelect.clear'), onClear: logic.clear } : undefined
        }
        onOpenChange={logic.onOpenChange}
      >
        <SelectionPanel mode="multi" {...logic.panel} />
      </SelectionTrigger>

      {hasChips ? (
        <SelectionChips
          values={rest.value}
          labelOf={logic.labelOf}
          maxVisibleChips={maxVisibleChips}
          isDisabled={isDisabled || isReadOnly}
          onRemove={logic.removeChip}
        />
      ) : null}
    </div>
  );
});
